/**
 * Musical Scales for Generative Music
 * Intervals are semitones from root note
 */

export const SCALES = {
  // Major scales
  major: [0, 2, 4, 5, 7, 9, 11], // Ionian - happy, bright
  lydian: [0, 2, 4, 6, 7, 9, 11], // Lydian - dreamy, floating
  mixolydian: [0, 2, 4, 5, 7, 9, 10], // Mixolydian - bluesy, rock

  // Minor scales
  minor: [0, 2, 3, 5, 7, 8, 10], // Natural minor - sad, melancholic
  dorian: [0, 2, 3, 5, 7, 9, 10], // Dorian - jazzy, sophisticated
  phrygian: [0, 1, 3, 5, 7, 8, 10], // Phrygian - dark, Spanish
  aeolian: [0, 2, 3, 5, 7, 8, 10], // Aeolian (natural minor)

  // Pentatonic (5 notes - always consonant)
  pentatonic: [0, 2, 4, 7, 9], // Major pentatonic - simple, folk
  pentatonicMinor: [0, 3, 5, 7, 10], // Minor pentatonic - blues, rock

  // Blues
  blues: [0, 3, 5, 6, 7, 10], // Blues scale - gritty, soulful

  // Exotic/World
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11], // Harmonic minor - classical, dramatic
  melodicMinor: [0, 2, 3, 5, 7, 9, 11], // Melodic minor - jazz
  wholetone: [0, 2, 4, 6, 8, 10], // Whole tone - dreamy, ambiguous
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], // All notes
} as const;

export type ScaleName = keyof typeof SCALES;

/**
 * Scale metadata for UI display
 */
export const SCALE_INFO: Record<ScaleName, { name: string; mood: string }> = {
  major: { name: 'Major', mood: 'Happy, bright' },
  lydian: { name: 'Lydian', mood: 'Dreamy, floating' },
  mixolydian: { name: 'Mixolydian', mood: 'Bluesy, rock' },
  minor: { name: 'Minor', mood: 'Sad, melancholic' },
  dorian: { name: 'Dorian', mood: 'Jazzy, sophisticated' },
  phrygian: { name: 'Phrygian', mood: 'Dark, Spanish' },
  aeolian: { name: 'Aeolian', mood: 'Natural minor' },
  pentatonic: { name: 'Pentatonic', mood: 'Simple, folk' },
  pentatonicMinor: { name: 'Pentatonic Minor', mood: 'Blues, rock' },
  blues: { name: 'Blues', mood: 'Gritty, soulful' },
  harmonicMinor: { name: 'Harmonic Minor', mood: 'Classical, dramatic' },
  melodicMinor: { name: 'Melodic Minor', mood: 'Jazz' },
  wholetone: { name: 'Whole Tone', mood: 'Dreamy, ambiguous' },
  chromatic: { name: 'Chromatic', mood: 'All notes' },
};

/**
 * Map mood value (0-100) to scale
 */
export function moodToScale(mood: number): ScaleName {
  // 0-20: Dark scales
  if (mood < 20) return 'phrygian';

  // 20-40: Minor scales
  if (mood < 40) return 'minor';

  // 40-60: Neutral scales
  if (mood < 60) return 'dorian';

  // 60-80: Major scales
  if (mood < 80) return 'major';

  // 80-100: Bright scales
  return 'lydian';
}

/**
 * Get scale intervals for a given scale name
 */
export function getScaleIntervals(scale: ScaleName): readonly number[] {
  return SCALES[scale];
}

/**
 * Get all available scale names
 */
export function getScaleNames(): ScaleName[] {
  return Object.keys(SCALES) as ScaleName[];
}
